/**
 * Where a synced document lives in the system it came from (MYAC-1640).
 *
 * Pure: it reads a `Document` and its `Datasource` and returns a URL plus an
 * i18n key under `document_source.*`, never text, so any surface (a table row,
 * a chip's menu) can say "Open in SharePoint" in its own locale. Every URL form
 * below was checked by clicking it on dev; a source this module cannot link
 * to returns `null` rather than a guess.
 */
import { DatasourceType, type Datasource } from '@/models/Datasource'
import { DocumentStatus, type Document } from '@/models/Document'
import { normalizeOdooBaseUrl } from './odoo'

export interface SourceLink {
  url: string
  /** A key under `document_source.*`. */
  labelKey: string
}

/**
 * Office for the web opens these through `Doc.aspx`. It cannot open anything
 * else (PDF, images, CSV), which `embed.aspx` previews instead.
 */
const OFFICE_EXTENSIONS: ReadonlySet<string> = new Set([
  'doc', 'docx', 'docm', 'dotx',
  'xls', 'xlsx', 'xlsm', 'xltx',
  'ppt', 'pptx', 'pptm', 'ppsx',
  'vsdx',
  'one',
])

/** The document was removed from its datasource, or is being removed. */
export function isDocumentGone(document: Document | null | undefined): boolean {
  return !document || document.status === DocumentStatus.TO_DELETE
}

/**
 * Uploaded by hand into MAC: there is no system to open it in, and the menu
 * says so in those words rather than as a missing link (MYAC-1644).
 */
export function isManualUpload(
  document: Document | null | undefined,
  datasource: Datasource | null | undefined,
): boolean {
  return document?.source_type === 'upload' || datasource?.type === DatasourceType.MANUAL_UPLOAD
}

export function buildSourceLink(
  document: Document | null | undefined,
  datasource: Datasource | null | undefined,
): SourceLink | null {
  if (!document) return null
  const link = linkFor(document, datasource)
  return link && isWebUrl(link.url) ? link : null
}

/**
 * Stored web URLs and site addresses come from sync configuration and are used
 * as is, so a `javascript:` or `data:` value there must never reach `window.open`.
 */
function isWebUrl(url: string): boolean {
  try {
    const { protocol } = new URL(url)
    return protocol === 'https:' || protocol === 'http:'
  } catch {
    return false
  }
}

function linkFor(document: Document, datasource: Datasource | null | undefined): SourceLink | null {
  switch (sourceOf(document)) {
    case 'o365':
      return sharePointLink(document, datasource)
    case 'gdrive':
      return googleDriveLink(document)
    case 'atlassian':
      return atlassianLink(document, datasource)
    case 'odoo':
      return odooLink(document, datasource)
    case 'qualishare':
      return qualishareLink(document, datasource)
    default:
      return null
  }
}

/**
 * Every sync service writes `source_type`; the metadata block is the fallback
 * for a document written before it did.
 */
function sourceOf(document: Document): string | undefined {
  if (document.source_type) return document.source_type
  if (document.o365_metadata) return 'o365'
  if (document.gdrive_metadata) return 'gdrive'
  if (document.atlassian_metadata) return 'atlassian'
  if (document.odoo_metadata) return 'odoo'
  if (document.qualishare_metadata) return 'qualishare'
  return undefined
}

function stripTrailingSlashes(url: string | undefined | null): string {
  return (url ?? '').trim().replace(/\/+$/, '')
}

function extensionOf(name: string | undefined): string {
  const dot = (name ?? '').lastIndexOf('.')
  return dot < 0 ? '' : (name ?? '').slice(dot + 1).toLowerCase()
}

// ── SharePoint / Office 365 ────────────────────────────────────────────────

function sharePointLink(document: Document, datasource: Datasource | null | undefined): SourceLink | null {
  const meta = document.o365_metadata
  if (!meta?.file_id) return null
  const guid = sharePointUniqueId(meta.file_id)
  const siteUrl = sharePointSiteUrl(meta.drive_id, meta.site_id, datasource)
  if (!guid || !siteUrl) return null
  const sourceName = meta.filename ?? document.original_filename ?? document.filename
  const url = OFFICE_EXTENSIONS.has(extensionOf(sourceName))
    ? `${siteUrl}/_layouts/15/Doc.aspx?sourcedoc=%7B${guid}%7D`
    : `${siteUrl}/_layouts/15/embed.aspx?UniqueId=${guid}`
  return { url, labelKey: 'document_source.open_in_sharepoint' }
}

/** The site whose drives hold the document; the stored site id when no drive matches. */
function sharePointSiteUrl(
  driveId: string | undefined,
  siteId: string | undefined,
  datasource: Datasource | null | undefined,
): string | null {
  const sites = datasource?.office365_metadata?.sites
  if (!(sites instanceof Map)) return null
  let bySiteId: string | null = null
  for (const [siteKey, site] of sites) {
    const webUrl = stripTrailingSlashes(site?.site_web_url)
    if (!webUrl) continue
    if (driveId && site.site_drives instanceof Map) {
      for (const [driveKey, drive] of site.site_drives) {
        if (driveKey === driveId || drive?.drive_id === driveId) return webUrl
      }
    }
    if (siteId && !bySiteId && (site.site_id === siteId || siteKey === siteId)) bySiteId = webUrl
  }
  return bySiteId
}

const BASE32_ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ234567'

/** RFC 4648 base32, padding optional. `null` on a character outside the alphabet. */
function base32Decode(input: string): Uint8Array | null {
  const out: number[] = []
  let buffer = 0
  let bits = 0
  for (const ch of input.replace(/=+$/, '')) {
    const index = BASE32_ALPHABET.indexOf(ch)
    if (index < 0) return null
    buffer = (buffer << 5) | index
    bits += 5
    if (bits >= 8) {
      bits -= 8
      out.push((buffer >>> bits) & 0xff)
      buffer &= (1 << bits) - 1
    }
  }
  return Uint8Array.from(out)
}

function hex(bytes: ArrayLike<number>): string {
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')
}

/**
 * The file's SharePoint UniqueId, from its Graph drive item id. The id is a
 * two-character prefix and base32 of 20 bytes, the GUID being bytes 4 to 20 in
 * Microsoft's mixed-endian order: Python's
 * `uuid.UUID(bytes_le=base64.b32decode(file_id[2:])[4:20])`.
 */
export function sharePointUniqueId(fileId: string): string | null {
  if (!fileId || fileId.length <= 2) return null
  const bytes = base32Decode(fileId.slice(2))
  if (!bytes || bytes.length < 20) return null
  const g = Array.from(bytes.subarray(4, 20))
  return [
    hex(g.slice(0, 4).reverse()),
    hex(g.slice(4, 6).reverse()),
    hex(g.slice(6, 8).reverse()),
    hex(g.slice(8, 10)),
    hex(g.slice(10, 16)),
  ].join('-')
}

// ── Google Drive ───────────────────────────────────────────────────────────

function googleDriveLink(document: Document): SourceLink | null {
  const fileId = document.gdrive_metadata?.file_id
  if (!fileId) return null
  return {
    url: `https://drive.google.com/file/d/${encodeURIComponent(fileId)}/view`,
    labelKey: 'document_source.open_in_google_drive',
  }
}

// ── Jira / Confluence ──────────────────────────────────────────────────────

function atlassianLink(document: Document, datasource: Datasource | null | undefined): SourceLink | null {
  const meta = document.atlassian_metadata
  if (!meta) return null
  const siteUrl = stripTrailingSlashes(datasource?.atlassian_metadata?.site_url)
  switch (meta.source_module) {
    case 'jira_issue':
      if (meta.web_url) return { url: meta.web_url, labelKey: 'document_source.open_in_jira' }
      return jiraIssueLink(siteUrl, meta.issue_key, 'document_source.open_in_jira')
    case 'jira_attachment':
      return jiraIssueLink(siteUrl, meta.issue_key, 'document_source.open_issue_in_jira')
    // The sync writes blog posts and databases with the page's own `web_url`.
    case 'confluence_page':
    case 'confluence_blogpost':
    case 'confluence_database':
      return meta.web_url ? { url: meta.web_url, labelKey: 'document_source.open_in_confluence' } : null
    case 'confluence_attachment': {
      const name = document.original_filename ?? document.filename
      if (!siteUrl || !meta.parent_id || !name) return null
      return {
        url: `${siteUrl}/wiki/download/attachments/${encodeURIComponent(meta.parent_id)}/${encodeURIComponent(name)}`,
        labelKey: 'document_source.open_in_confluence',
      }
    }
    default:
      return null
  }
}

function jiraIssueLink(siteUrl: string, issueKey: string | undefined, labelKey: string): SourceLink | null {
  if (!siteUrl || !issueKey) return null
  return { url: `${siteUrl}/browse/${encodeURIComponent(issueKey)}`, labelKey }
}

// ── Odoo ───────────────────────────────────────────────────────────────────

function odooLink(document: Document, datasource: Datasource | null | undefined): SourceLink | null {
  const meta = document.odoo_metadata
  const base = normalizeOdooBaseUrl(datasource?.odoo_metadata?.base_url || datasource?.odoo_credentials?.url || '')
  if (!base || !meta?.odoo_model || typeof meta.odoo_id !== 'number' || !Number.isFinite(meta.odoo_id)) return null
  return { url: `${base}/odoo/${meta.odoo_model}/${meta.odoo_id}`, labelKey: 'document_source.see_in_odoo' }
}

// ── Qualishare ─────────────────────────────────────────────────────────────

function qualishareLink(document: Document, datasource: Datasource | null | undefined): SourceLink | null {
  const meta = document.qualishare_metadata
  const base = stripTrailingSlashes(datasource?.qualishare_metadata?.base_url || datasource?.qualishare_credentials?.url)
  const listGuid = (meta?.sp_list_guid ?? '').replace(/[{}]/g, '').trim()
  const itemId = meta?.sp_item_id
  if (!base || !listGuid || typeof itemId !== 'number' || !Number.isFinite(itemId)) return null
  // A list on the root site has no segment, as in the sync's own client.
  const site = (meta?.site ?? '').replace(/^\/+|\/+$/g, '')
  const siteUrl = site ? `${base}/${site}` : base
  return {
    url: `${siteUrl}/_layouts/15/listform.aspx?PageType=4&ListId=%7B${listGuid}%7D&ID=${itemId}`,
    labelKey: 'document_source.open_in_qualishare',
  }
}
